// Ported from `app/atlas/[level]/page.tsx` in
// `git@git.fugl.dev:sks/yominoki.git` @ `515ce239`.
//
// The header chrome is that page's, unchanged. What moved is the two guards
// around it: `notFound()` on an unknown slug becomes a rendered "no such
// level" (a client route cannot 404 itself, and `SLUG_TO_LEVEL` is shared with
// the endpoint so the two cannot disagree), and `redirect("/")` on a missing
// session becomes `<Navigate replace>` — but only once the session is KNOWN,
// because redirecting while the answer is outstanding would bounce every
// signed-in reader off this page on load.
//
// `import "../atlas.css"` is gone with the route-local stylesheet; those rules
// live in ../index.css now.

import { Navigate, useParams } from "react-router";
import { isAtlasSlug } from "../api/contract";
import { AtlasMap } from "../components/atlas/atlas-map";
import { LevelSelector } from "../components/atlas/level-selector";
import { useResource } from "../hooks/use-resource";
import { useSession } from "../hooks/use-session";
import { getAtlas } from "../lib/api";

export function AtlasPage() {
  const { level } = useParams();
  const slug = (level ?? "").toLowerCase();
  const known = isAtlasSlug(slug);

  const session = useSession();
  const signedIn = session.status === "signed-in";

  const { resource } = useResource(
    (signal) =>
      known && signedIn ? getAtlas(slug, signal) : Promise.resolve([]),
    [slug, known, signedIn],
  );

  if (!known) {
    return (
      <main className="bg-paper text-ink flex h-svh items-center justify-center">
        <p className="n5-marginalia">no such level</p>
      </main>
    );
  }
  if (session.status === "unknown") return null;
  if (session.status === "signed-out") return <Navigate to="/" replace />;

  const entries = resource.status === "ready" ? resource.data : [];

  return (
    <main className="atlas-page bg-paper text-ink flex h-svh flex-col">
      <div className="border-rule flex items-center justify-between gap-6 border-b px-4 py-1.5">
        <div className="flex items-baseline gap-3">
          <span
            className="text-base"
            style={{
              fontFamily: "var(--font-jp-display), serif",
              letterSpacing: "0.05em",
              color: "var(--ink)",
            }}
          >
            蔵書
          </span>
          <span className="n5-marginalia">
            Atlas · {slug.toUpperCase()} ·{" "}
            {resource.status === "loading"
              ? "surveying…"
              : `${String(entries.length)} tiles`}
          </span>
        </div>
        <LevelSelector active={slug} />
      </div>

      <div className="relative min-h-0 flex-1">
        <AtlasMap entries={entries} />
      </div>
    </main>
  );
}
